/**
 * Planning de cobertura — capa de datos.
 *
 * La UI (src/components/PlanningCobertura.tsx) solo conoce la interfaz
 * `PlanningRepo`, nunca el backend. Hoy usamos `repoLocal` (guardado en este
 * navegador, NO compartido) para poder probar la UI ya. Cuando el compañero de
 * datos implemente `PlanningRepo` con Firestore, la UI no cambia ni una línea.
 *
 * Ver docs/planning-cobertura.md.
 */

import type { Edicion } from '@/config/ediciones';

export type TipoTarea = 'tarea' | 'carga-datos';

export interface Tarea {
  id: string;
  /** A qué campeonato pertenece (slug de ediciones.ts). */
  edicionId: string;
  /** Día en el que cae, ISO yyyy-mm-dd. */
  fecha: string;
  texto: string;
  descripcion: string;
  tipo: TipoTarea;
  responsable: string | null;
  colaboradores: string[];
  /** id de la herramienta del panel a la que va ligada (para el enlace directo). */
  herramientaId: string | null;
  /** ids de tareas que deben estar hechas antes que esta (dependencias). */
  dependeDe: string[];
  hecha: boolean;
  /** Si vino de la plantilla automática o la añadió alguien a mano. */
  origen: 'plantilla' | 'manual';
  creadaEn: number;
  actualizadaEn: number;
}

export type NuevaTarea = {
  /** Opcional: id determinista (lo usa la plantilla para poder referenciar
   *  dependencias). Si no se da, se genera uno. */
  id?: string;
  edicionId: string;
  fecha: string;
  texto: string;
  descripcion?: string;
  tipo?: TipoTarea;
  responsable?: string | null;
  colaboradores?: string[];
  herramientaId?: string | null;
  dependeDe?: string[];
  origen?: 'plantilla' | 'manual';
};

/** Contrato que separa la UI de la fuente de datos. */
export interface PlanningRepo {
  listar(edicionId: string): Promise<Tarea[]>;
  crear(t: NuevaTarea): Promise<Tarea>;
  actualizar(id: string, cambios: Partial<Tarea>): Promise<void>;
  borrar(id: string): Promise<void>;
  /** Opcional: tiempo real. Devuelve función para desuscribirse. */
  suscribir?(edicionId: string, cb: (tareas: Tarea[]) => void): () => void;
}

// ---- Utilidades de fecha (ISO yyyy-mm-dd, sin zona horaria) ----

function aIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

/** Suma (o resta, con n negativo) días a una fecha ISO. */
export function addDias(iso: string, n: number): string {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return aIso(d);
}

/** Todos los días ISO entre inicio y fin, ambos incluidos (tope de seguridad). */
function rangoDias(inicio: string, fin: string): string[] {
  const out: string[] = [];
  const finMs = new Date(fin + 'T00:00:00').getTime();
  let cur = inicio;
  let guarda = 0;
  while (new Date(cur + 'T00:00:00').getTime() <= finMs && guarda < 60) {
    out.push(cur);
    cur = addDias(cur, 1);
    guarda++;
  }
  return out;
}

/**
 * Plantilla de tareas por defecto, calculada desde las fechas de la edición:
 * - Carga de datos → 3 días antes del inicio (bloquea a las demás).
 * - Previa del campeonato → 2 días antes (depende de la carga de datos).
 * - Cobertura de la jornada y Post jornada → uno por cada día del campeonato.
 * - Post campeonato → 1 día después del fin.
 *
 * Los ids son deterministas (`<edicion>__<clave>`) para poder declarar las
 * dependencias entre tareas de la plantilla.
 */
export function plantillaPorDefecto(ed: Edicion): NuevaTarea[] {
  const id = (clave: string) => `${ed.id}__${clave}`;
  const cargaId = id('carga-datos');
  const tareas: NuevaTarea[] = [];

  tareas.push({
    id: cargaId,
    edicionId: ed.id,
    fecha: addDias(ed.fechaInicio, -3),
    texto: 'Carga de datos',
    tipo: 'carga-datos',
    origen: 'plantilla',
    descripcion:
      'Cargar y cruzar los datos de la competición. Nada se puede elaborar hasta que esté hecho.',
  });

  tareas.push({
    id: id('previa'),
    edicionId: ed.id,
    fecha: addDias(ed.fechaInicio, -2),
    texto: 'Previa del campeonato',
    origen: 'plantilla',
    herramientaId: 'previa-articulo',
    dependeDe: [cargaId],
    descripcion: 'Artículo de previa a partir de inscritos y marcas a batir.',
  });

  for (const dia of rangoDias(ed.fechaInicio, ed.fechaFin)) {
    tareas.push({
      id: id(`cobertura__${dia}`),
      edicionId: ed.id,
      fecha: dia,
      texto: 'Cobertura de la jornada',
      origen: 'plantilla',
      herramientaId: 'resultados',
      dependeDe: [cargaId],
    });
    tareas.push({
      id: id(`postjornada__${dia}`),
      edicionId: ed.id,
      fecha: dia,
      texto: 'Post jornada',
      origen: 'plantilla',
      herramientaId: 'post-jornada',
    });
  }

  tareas.push({
    id: id('postcampeonato'),
    edicionId: ed.id,
    fecha: addDias(ed.fechaFin, 1),
    texto: 'Post campeonato',
    origen: 'plantilla',
    herramientaId: 'post-campeonato',
  });

  return tareas;
}

/** Siembra la plantilla SOLO si esa edición no tiene tareas todavía (no pisa
 *  cambios del equipo). */
export async function sembrarSiVacio(repo: PlanningRepo, ed: Edicion): Promise<void> {
  const existentes = await repo.listar(ed.id);
  if (existentes.length > 0) return;
  for (const n of plantillaPorDefecto(ed)) {
    await repo.crear(n);
  }
}

// ---- Implementación PROVISIONAL local (localStorage, por navegador) ----
// NO compartida. Se sustituye por la de Firestore sin tocar la UI.

const KEY = 'planning-cobertura-v2';

function leerTodo(): Tarea[] {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(KEY) : null;
    return raw ? (JSON.parse(raw) as Tarea[]) : [];
  } catch {
    return [];
  }
}

function guardarTodo(ts: Tarea[]): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(KEY, JSON.stringify(ts));
  } catch {
    /* almacenamiento no disponible: se ignora */
  }
}

function uid(): string {
  return 't_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
}

export const repoLocal: PlanningRepo = {
  async listar(edicionId) {
    return leerTodo().filter((t) => t.edicionId === edicionId);
  },
  async crear(n) {
    const ahora = Date.now();
    const t: Tarea = {
      id: n.id ?? uid(),
      edicionId: n.edicionId,
      fecha: n.fecha,
      texto: n.texto,
      descripcion: n.descripcion ?? '',
      tipo: n.tipo ?? 'tarea',
      responsable: n.responsable ?? null,
      colaboradores: n.colaboradores ?? [],
      herramientaId: n.herramientaId ?? null,
      dependeDe: n.dependeDe ?? [],
      hecha: false,
      origen: n.origen ?? 'manual',
      creadaEn: ahora,
      actualizadaEn: ahora,
    };
    const todo = leerTodo();
    todo.push(t);
    guardarTodo(todo);
    return t;
  },
  async actualizar(id, cambios) {
    const todo = leerTodo();
    const i = todo.findIndex((t) => t.id === id);
    if (i >= 0) {
      todo[i] = { ...todo[i], ...cambios, actualizadaEn: Date.now() };
      guardarTodo(todo);
    }
  },
  async borrar(id) {
    guardarTodo(leerTodo().filter((t) => t.id !== id));
  },
};
